import { H4, P } from "@/components/ui/typography";
import { ContainerPrimary } from "../ui";
import { useParams } from "react-router-dom";
import { MISSING_ID_IN_ROUTE, ROLE } from "@/utils/constant";
import {
  useDownloadMemoActive,
  useIntegrationMemoActives,
  useSendMemoActiveEmail,
  useUpdateIntegrationMemoActive,
  useUpdateSendMemoActive,
} from "@/api/generate-hpdb";
import {
  swalError,
  swalErrorDefault,
  swalLoading,
  swalSuccess,
} from "@/utils/lib/swal";
import {
  DefaultButton,
  DownloadButton,
  SubmitButton,
} from "@/components/ui/button";
import { convertUppercaseLabelToCapitalize } from "../utils/convertUppercaseLabelToCapitalize";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { useRFS } from "@/api/rfs";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import axios, { AxiosError } from "axios";
import { useProfileByKeycloakId } from "@/api/auth";

export function GenerateMemoActiveHandler({
  isReadOnly,
}: {
  isReadOnly: boolean;
}) {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { roles, id: userId } = useCurrentUser();
  const { data: profile } = useProfileByKeycloakId({ keycloakId: userId });

  const { data: rfs } = useRFS({ id });
  const { data: integrationMemoActives } = useIntegrationMemoActives({
    rfsId: id,
  });
  const integrationMemoActive = integrationMemoActives?.[0];

  const { mutate: download, isPending: isDownloading } =
    useDownloadMemoActive();
  const { mutate: updateMemo, isPending } = useUpdateIntegrationMemoActive();
  const { mutate: sendMemo } = useSendMemoActiveEmail();
  const { mutate: updateSendMemo } = useUpdateSendMemoActive();

  const canAccess =
    hasCommonElement(roles, [
      ROLE.ADMINISTRATOR,
      ROLE.VALIDATOR_DOCUMENT_OFFICER,
      ROLE.MONITORING_TEAM,
    ]) || profile?.companyCode === rfs?.picVendor.companyCode;

  const canSendMemo = hasCommonElement(roles, [
    ROLE.ADMINISTRATOR,
    ROLE.VALIDATOR_DOCUMENT_OFFICER,
  ]);

  function handleDownload() {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }
    download(
      {
        id,
        name:
          integrationMemoActive?.noMemo?.split("/")?.[0] +
          "-Memo Active-" +
          rfs?.picVendor?.company +
          "-" +
          rfs?.fdtId +
          "-" +
          rfs?.clusterId +
          "-" +
          rfs?.projectId +
          "-" +
          new Date().toISOString().split("T")[0].replaceAll("-", "") +
          ".pdf",
      },
      {
        onSuccess: () => {
          swalSuccess.fire({
            text: "Memo Active has been downloaded successfully",
          });
        },
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  }

  function handleSendMemo() {
    swalLoading.fire();
    sendMemo(
      {
        id: id!,
      },
      {
        onSuccess: (data) => {
          // Update send memo active status
          if (!integrationMemoActive?.sendMemoActive && integrationMemoActive) {
            // only if the send memo active status is false
            updateSendMemo(
              {
                id: integrationMemoActive.id,
                data: {
                  sendMemoActive: true,
                },
              },
              {
                onError: (error) => {
                  swalError.fire({
                    text: error.message,
                  });
                },
              },
            );
          }

          swalSuccess.fire({
            text: data?.data ?? "Memo has been sent",
          });
        },
        onError: (error) => {
          const message = axios.isAxiosError(error)
            ? (error as AxiosError<{ data: string }>).response?.data?.data
            : error.message;
          swalError.fire({
            text: message,
          });
        },
      },
    );
  }

  const handleUpdate = () => {
    updateMemo(
      {
        id: id,
      },
      {
        onSuccess: (result) => {
          const originalData = result.data.original;
          if (originalData.status == "error") {
            swalError.fire({
              text: "Failed update Memo",
            });
          } else {
            swalSuccess.fire({
              text: "Successfully update Memo",
            });
          }
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  };

  return (
    <ContainerPrimary>
      <div className="flex-grow whitespace-nowrap">
        <H4>Generate Memo Active</H4>
      </div>
      <div className="flex items-center gap-x-2">
        <P className="flex-grow text-axiata-blue dark:text-neutral-300">
          {convertUppercaseLabelToCapitalize(integrationMemoActive?.status)}
        </P>
        <SubmitButton
          onClick={handleUpdate}
          loading={isPending}
          className="ml-auto whitespace-nowrap"
          disabled={isReadOnly || !canAccess || !integrationMemoActive}
        >
          Update
        </SubmitButton>
        {canAccess && (
          <DownloadButton
            onClick={handleDownload}
            loading={isDownloading}
            className="w-full"
            disabled={integrationMemoActive?.status !== "SUCCESS" && !canAccess}
          >
            Download
          </DownloadButton>
        )}
        {canSendMemo && (
          <DefaultButton
            mood="axiata-purple-outline"
            className="whitespace-nowrap"
            onClick={handleSendMemo}
            disabled={
              integrationMemoActive?.status !== "SUCCESS" && !canSendMemo
            }
          >
            Send Memo
          </DefaultButton>
        )}
      </div>
    </ContainerPrimary>
  );
}
